/**
 * Shared date-range picker used by the Mailer Dashboard, Campaigns table,
 * and Marketing Spend ledger. Matches the split-panel design: presets on
 * the left, start/end date inputs + Apply/Cancel on the right.
 *
 * Public API
 *   <DateRangePicker
 *     value={{ rangeId, customRange }}
 *     onApply={({ rangeId, customRange }) => ...}
 *   />
 *
 * `rangeToIsoParams(value)` returns the `{ start, end }` ISO-date params
 * the backend endpoints already accept.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar as CalIcon, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

// ---- presets -------------------------------------------------------------
export const RANGE_PRESETS = [
  { id: 'mtd',       label: 'Month to Date'   },
  { id: 'lastMonth', label: 'Last Month'      },
  { id: 'thisWeek',  label: 'This Week'       },
  { id: '7',         label: 'Last 7 days'     },
  { id: '30',        label: 'Last 30 days'    },
  { id: '90',        label: 'Last 90 days'    },
  { id: 'qtd',       label: 'Quarter to Date' },
  { id: 'ytd',       label: 'Year to Date'    },
  { id: 'all',       label: 'All time'        },
  { id: 'custom',    label: 'Custom range'    },
];

const DEFAULT_VALUE = { rangeId: 'all', customRange: { from: null, to: null } };

// ---- date helpers --------------------------------------------------------
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

function presetDates(id) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (id) {
    case 'mtd':       return { from: startOfDay(new Date(y, m, 1)),     to: endOfDay(now) };
    case 'lastMonth': return { from: startOfDay(new Date(y, m - 1, 1)), to: endOfDay(new Date(y, m, 0)) };
    case 'thisWeek': {
      const d = now.getDay();                          // 0 = Sun
      const monday = new Date(y, m, now.getDate() - ((d + 6) % 7));
      return { from: startOfDay(monday), to: endOfDay(now) };
    }
    case '7':   return { from: startOfDay(new Date(now.getTime() - 6  * 86400000)), to: endOfDay(now) };
    case '30':  return { from: startOfDay(new Date(now.getTime() - 29 * 86400000)), to: endOfDay(now) };
    case '90':  return { from: startOfDay(new Date(now.getTime() - 89 * 86400000)), to: endOfDay(now) };
    case 'qtd': {
      const qStart = new Date(y, Math.floor(m / 3) * 3, 1);
      return { from: startOfDay(qStart), to: endOfDay(now) };
    }
    case 'ytd': return { from: startOfDay(new Date(y, 0, 1)), to: endOfDay(now) };
    case 'all': return { from: null, to: null };
    default:    return { from: null, to: null };
  }
}

const ymd = (d) => {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const mdy = (d) => {
  if (!d) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

// Convert a value `{ rangeId, customRange }` into the `{ start, end }` ISO
// params the backend endpoints already accept. `rangeId === 'all'` returns
// an empty object (no date filter).
export function rangeToIsoParams(value) {
  const v = value || DEFAULT_VALUE;
  if (!v.rangeId || v.rangeId === 'all') return {};
  const dates = v.rangeId === 'custom' ? (v.customRange || {}) : presetDates(v.rangeId);
  const out = {};
  if (dates.from) out.start = ymd(dates.from);
  if (dates.to)   out.end   = ymd(dates.to);
  return out;
}

// Pretty label rendered on the trigger button.
function triggerLabel(value) {
  const id = value?.rangeId || 'all';
  if (id === 'custom') {
    const { from, to } = value.customRange || {};
    if (from && to) return `${mdy(from)} – ${mdy(to)}`;
    if (from)       return `${mdy(from)} – …`;
    return 'Custom range';
  }
  return (RANGE_PRESETS.find((p) => p.id === id) || RANGE_PRESETS[0]).label;
}

// ---- component -----------------------------------------------------------
export default function DateRangePicker({ value, onApply, testIdPrefix = 'daterange' }) {
  const safeValue = value || DEFAULT_VALUE;
  const [open, setOpen] = useState(false);
  // Working copy edited inside the popover until the user clicks Apply.
  const [draft, setDraft] = useState(safeValue);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  // Sync draft → external whenever the popover opens. Using a ref instead
  // of state-in-effect avoids the React 19 hooks lint rule while keeping
  // the draft scoped to a single popover open lifecycle.
  const lastOpenRef = useRef(false);
  useEffect(() => {
    if (open && !lastOpenRef.current) {
      setDraft({
        rangeId: safeValue.rangeId,
        customRange: safeValue.rangeId === 'custom'
          ? { ...(safeValue.customRange || {}) }
          : presetDates(safeValue.rangeId),
      });
    }
    lastOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Click-away close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const draftDates = useMemo(() => {
    if (draft.rangeId === 'custom') return draft.customRange || { from: null, to: null };
    return presetDates(draft.rangeId);
  }, [draft]);

  const pickPreset = (id) => {
    if (id === 'custom') {
      setDraft({ rangeId: 'custom', customRange: draft.customRange?.from ? draft.customRange : { from: null, to: null } });
    } else {
      setDraft({ rangeId: id, customRange: presetDates(id) });
    }
  };

  const editDate = (which, str) => {
    // Accepts YYYY-MM-DD from <input type="date">.
    const d = str ? new Date(`${str}T00:00:00`) : null;
    const next = { ...(draft.customRange || draftDates) };
    next[which] = d;
    setDraft({ rangeId: 'custom', customRange: next });
  };

  const apply = () => {
    onApply?.({
      rangeId: draft.rangeId,
      customRange: draft.rangeId === 'custom' ? draft.customRange : { from: null, to: null },
    });
    setOpen(false);
  };

  const cancel = () => setOpen(false);

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testIdPrefix}-trigger`}
        className="h-10 px-4 rounded-full border hairline bg-white dark:bg-neutral-950 hover:border-neutral-400 dark:hover:border-neutral-600 inline-flex items-center gap-2 text-[12.5px] font-semibold text-neutral-700 dark:text-neutral-200 transition"
      >
        <CalIcon className="h-4 w-4 text-neutral-500" />
        <span className="whitespace-nowrap">{triggerLabel(safeValue)}</span>
        <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
      </button>

      {open && (
        <div
          ref={popRef}
          data-testid={`${testIdPrefix}-panel`}
          className="absolute right-0 top-full mt-2 z-40 flex rounded-2xl border hairline bg-white dark:bg-neutral-950 shadow-[0_24px_64px_-20px_rgba(0,0,0,0.35)] overflow-hidden"
          style={{ minWidth: 540 }}
        >
          {/* LEFT — presets */}
          <div className="w-[200px] py-2 border-r hairline bg-neutral-50/40 dark:bg-neutral-900/40">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pickPreset(p.id)}
                data-testid={`${testIdPrefix}-preset-${p.id}`}
                className={cn(
                  'w-full text-left px-4 py-2.5 text-[12.5px] font-semibold transition whitespace-nowrap',
                  draft.rangeId === p.id
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
                    : 'text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100/70 dark:hover:bg-white/[0.04]',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* RIGHT — start / end inputs + apply */}
          <div className="flex-1 p-5 flex flex-col gap-4 min-w-[300px]">
            <Field
              label="START"
              value={draftDates.from ? ymd(draftDates.from) : ''}
              onChange={(s) => editDate('from', s)}
              testId={`${testIdPrefix}-start`}
            />
            <Field
              label="END"
              value={draftDates.to ? ymd(draftDates.to) : ''}
              onChange={(s) => editDate('to', s)}
              testId={`${testIdPrefix}-end`}
            />
            <div className="flex items-center justify-end gap-2 pt-2 mt-auto">
              <button
                type="button"
                onClick={cancel}
                data-testid={`${testIdPrefix}-cancel`}
                className="h-9 px-4 rounded-lg border hairline bg-white dark:bg-neutral-950 text-[12.5px] font-semibold text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-white/[0.04] transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={apply}
                data-testid={`${testIdPrefix}-apply`}
                className="h-9 px-5 rounded-lg bg-neutral-900 text-white dark:bg-white dark:text-neutral-900 text-[12.5px] font-bold hover:opacity-90 transition"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- internal field ------------------------------------------------------
function Field({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.22em] font-bold text-neutral-500 dark:text-neutral-400 mb-1.5">
        {label}
      </span>
      <div className="relative">
        <CalIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none z-[1]" />
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          // `color-scheme: dark` flips the native date-picker chrome to a
          // dark calendar icon + dark popover when the html element has
          // `.dark`. Without it Chrome shows a hard-coded light calendar
          // pill that's invisible on the dark popover background.
          style={{ colorScheme: 'inherit' }}
          className="w-full h-11 pl-10 pr-3 rounded-xl border hairline bg-white dark:bg-neutral-900 text-[13px] font-semibold text-neutral-800 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 dark:focus:ring-white/10 [color-scheme:light] dark:[color-scheme:dark]"
        />
      </div>
    </label>
  );
}
